import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { deriveClientKey, isWeakPin, loginKey, readRecoveryFile } from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { AuthLayout } from "../components/AuthLayout.js";
import { OneTimeCodeField } from "../components/OneTimeCodeField.js";
import { Banner, Button, Field, inputClass } from "../components/ui.js";
import { auth } from "../copy/auth.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { useSession } from "../session.js";

export function PinRecoverScreen() {
  return (
    <PinCodeForm
      title={auth.pinForgot.title}
      intro={auth.pinForgot.introRecover}
      path="/api/auth/recover"
      codeField="recovery_code"
      codeLabel={auth.pinForgot.yourCode}
      fromFile
      other={{ to: "/pin/reset", text: auth.pinForgot.tryReset }}
    />
  );
}

export function PinResetScreen() {
  return (
    <PinCodeForm
      title={auth.pinForgot.title}
      intro={auth.pinForgot.introReset}
      path="/api/auth/reset"
      codeField="reset_code"
      codeLabel={auth.pinForgot.teacherCode}
      other={{ to: "/pin/recover", text: auth.pinForgot.tryRecover }}
    />
  );
}

function PinCodeForm({
  title,
  intro,
  path,
  codeField,
  codeLabel,
  fromFile = false,
  other,
}: {
  title: string;
  intro: string;
  path: "/api/auth/recover" | "/api/auth/reset";
  codeField: "recovery_code" | "reset_code";
  codeLabel: string;
  /** Offer to read the file «Schreib das auf» saved (issue #125). */
  fromFile?: boolean;
  /** The other form, offered when the code is refused: the two look alike (issue #123). */
  other: { to: "/pin/recover" | "/pin/reset"; text: string };
}) {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const location = useLocation();
  const carried = location.state as { name?: string; code?: string } | null;
  const [name, setName] = useState(carried?.name ?? "");
  const [code, setCode] = useState(carried?.code ?? "");
  // Read once, then gone from the history: «Zurück» on a shared iPad must not
  // bring the code back.
  useEffect(() => {
    if (carried) navigate(location.pathname, { replace: true, state: null });
  }, [carried, location.pathname, navigate]);
  const [wrongCode, setWrongCode] = useState(false);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setWrongCode(false);
    if (isWeakPin(pin)) {
      setError(errors.weakPin);
      return;
    }
    if (pin !== pin2) {
      setError(errors.pinMismatch);
      return;
    }
    setBusy(true);
    try {
      const handle = loginKey(name);
      const dk = await deriveClientKey(pin, handle);
      await api(path, {
        method: "POST",
        body: JSON.stringify({ handle, [codeField]: code, dk }),
      });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
      setWrongCode(err instanceof ApiError && err.code === "recovery_failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      mood="think"
      title={title}
      intro={intro}
      footer={
        <p className="text-center text-sm lg:text-left">
          <Link to="/login" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
            {auth.pinForgot.backToLogin}
          </Link>
        </p>
      }
    >
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        {error ? (
          <Banner>
            {error}
            {wrongCode ? (
              <>
                {" "}
                <Link to={other.to} replace state={{ name, code }} className="font-semibold underline underline-offset-4">
                  {other.text}
                </Link>
              </>
            ) : null}
          </Banner>
        ) : null}
        {fromFile ? (
          <CodeFileButton
            onRead={(read) => {
              setError(null);
              setWrongCode(false);
              if (read.name) setName(read.name);
              setCode(read.code);
            }}
            onFail={() => {
              setWrongCode(false);
              setError(auth.pinForgot.notCodeFile);
            }}
          />
        ) : null}
        <Field label={auth.pinForgot.yourName}>
          <input
            className={inputClass()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="TanzendesBlauesPferd"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </Field>
        <OneTimeCodeField label={codeLabel} value={code} onChange={setCode} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={auth.pinForgot.newPin}>
            <input
              className={inputClass()}
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="new-password"
              required
            />
          </Field>
          <Field label={auth.pinForgot.pinAgain}>
            <input
              className={inputClass()}
              type="password"
              value={pin2}
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="new-password"
              required
            />
          </Field>
        </div>
        <Button type="submit" disabled={busy}>
          {busy ? common.saving : auth.pinForgot.save}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** The saved code file is a few lines; anything much bigger is another file. */
const CODE_FILE_MAX_BYTES = 4096;

/**
 * Reads `vocifuchs-code.txt` on the device and hands over name and code
 * (issue #125). The file goes nowhere: only the code is sent, on «PIN
 * speichern», as if it had been typed.
 */
function CodeFileButton({
  onRead,
  onFail,
}: {
  onRead: (read: { name: string | null; code: string }) => void;
  onFail: () => void;
}) {
  const picker = useRef<HTMLInputElement>(null);

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const read = file.size <= CODE_FILE_MAX_BYTES ? readRecoveryFile(await file.text().catch(() => "")) : null;
    if (read?.code) onRead({ name: read.name, code: read.code });
    else onFail();
  };

  return (
    <div className="space-y-1.5">
      <Button variant="ghost" onClick={() => picker.current?.click()}>
        {auth.pinForgot.loadFile}
      </Button>
      <p className="text-sm text-ink-soft">{auth.pinForgot.loadFileHint}</p>
      <input
        ref={picker}
        type="file"
        accept=".txt,text/plain"
        className="hidden"
        aria-label={auth.pinForgot.loadFile}
        onChange={(e) => void pick(e)}
      />
    </div>
  );
}
